import React, { useState } from 'react';
import { Loader2, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import { useCanvasStore } from './canvas-store';
import { useActiveProjectImages } from './store-selectors';

export interface WorkflowTaskStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progressMessage?: string;
}

export interface WorkflowTaskCardData {
  taskId: string;
  label: string;
  toolName: string;
  status: 'running' | 'completed' | 'failed';
  progressMessage?: string;
  summary?: string;
  thumbnails?: string[];
  error?: string;
}

interface WorkflowTaskCardProps {
  card: WorkflowTaskCardData;
}

export function WorkflowTaskCard({ card }: WorkflowTaskCardProps) {
  const [showProcess, setShowProcess] = useState(false);
  // Subscribe to just the two fields we need. Clicking a thumbnail finds the
  // matching canvas image by URL and pans the viewport to it — same pattern
  // as the chat grid thumbnails (canvas-chat.tsx ~line 4683).
  const images = useActiveProjectImages();
  const panToImage = useCanvasStore((s) => s.panToImage);

  const isRunning = card.status === 'running';
  const isCompleted = card.status === 'completed';
  const isFailed = card.status === 'failed';

  return (
    <div
      data-testid={`workflow-task-card-${card.taskId}`}
      style={{
        background: '#1F2937',
        borderRadius: 12,
        padding: '14px 16px',
        marginBottom: 8,
        color: 'white',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flexShrink: 0 }}>
          {isRunning && (
            <Loader2
              className="animate-spin"
              style={{ width: 16, height: 16, color: '#6B7FE6' }}
            />
          )}
          {isCompleted && (
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: '#22C55E',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircle2 style={{ width: 10, height: 10, color: 'white' }} />
            </div>
          )}
          {isFailed && (
            <XCircle style={{ width: 16, height: 16, color: '#EF4444' }} />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <span
            data-testid={`workflow-task-label-${card.taskId}`}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: isFailed ? '#FCA5A5' : 'white',
            }}
          >
            {card.label}
          </span>
          {isRunning && card.progressMessage && (
            <p
              style={{
                margin: '2px 0 0',
                fontSize: 11,
                color: '#9CA3AF',
              }}
            >
              {card.progressMessage}
            </p>
          )}
          {isCompleted && card.summary && (
            <p
              data-testid={`workflow-task-summary-${card.taskId}`}
              style={{
                margin: '2px 0 0',
                fontSize: 11,
                color: '#D1D5DB',
              }}
            >
              {card.summary}
            </p>
          )}
          {isFailed && card.error && (
            <p
              style={{
                margin: '2px 0 0',
                fontSize: 11,
                color: '#FCA5A5',
              }}
            >
              {card.error}
            </p>
          )}
        </div>

        {isCompleted && (
          <button
            data-testid={`workflow-task-toggle-${card.taskId}`}
            onClick={() => setShowProcess((v) => !v)}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: 6,
              padding: '4px 8px',
              fontSize: 11,
              color: '#D1D5DB',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
            }}
          >
            {showProcess ? 'Hide' : 'Show'} process
            <ChevronDown
              style={{
                width: 12,
                height: 12,
                transform: showProcess ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </button>
        )}
      </div>

      {isCompleted && card.thumbnails && card.thumbnails.length > 0 && (
        <div
          data-testid={`workflow-task-thumbnails-${card.taskId}`}
          style={{
            display: 'flex',
            gap: 6,
            marginTop: 10,
            flexWrap: 'wrap',
          }}
        >
          {card.thumbnails.map((url, i) => {
            const match = images.find((img) => img.src === url);
            const clickable = match !== undefined;
            return (
              <img
                key={i}
                src={url}
                alt=""
                title={clickable ? 'Click to jump to this ad on the canvas' : undefined}
                style={{
                  width: 64,
                  height: 64,
                  objectFit: 'cover',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.15)',
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onClick={clickable ? () => panToImage(match!.id) : undefined}
                onMouseEnter={(e) => {
                  if (clickable) {
                    (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.05)';
                    (e.currentTarget as HTMLImageElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)';
                  (e.currentTarget as HTMLImageElement).style.boxShadow = 'none';
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            );
          })}
        </div>
      )}

      {showProcess && isCompleted && (
        <div
          style={{
            marginTop: 10,
            padding: '8px 10px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 6,
            fontSize: 11,
            color: '#9CA3AF',
            lineHeight: 1.5,
          }}
        >
          {card.summary || 'Task completed.'}
        </div>
      )}

      {isRunning && (
        <div
          style={{
            marginTop: 8,
            height: 2,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: '40%',
              background: '#6B7FE6',
              borderRadius: 1,
              animation: 'wf-task-pulse 1.5s ease-in-out infinite',
            }}
          />
          <style>{`
            @keyframes wf-task-pulse {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(350%); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}

interface WorkflowTaskGroupProps {
  cards: WorkflowTaskCardData[];
  planTitle?: string;
}

export function WorkflowTaskGroup({ cards, planTitle }: WorkflowTaskGroupProps) {
  if (cards.length === 0) return null;

  const allComplete = cards.every((c) => c.status !== 'running');
  const anyFailed = cards.some((c) => c.status === 'failed');
  const allSucceeded = cards.every((c) => c.status === 'completed');

  return (
    <div
      data-testid="workflow-task-group"
      style={{ marginTop: 8, marginBottom: 8 }}
    >
      {planTitle && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#8C8578',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {planTitle}
          </span>
          {allComplete && (
            <span
              style={{
                fontSize: 10,
                color: anyFailed && !allSucceeded ? '#F59E0B' : '#22C55E',
                fontWeight: 600,
              }}
            >
              {anyFailed && !allSucceeded
                ? 'Partial'
                : allSucceeded
                ? 'Complete'
                : ''}
            </span>
          )}
        </div>
      )}
      {cards.map((card) => (
        <WorkflowTaskCard key={card.taskId} card={card} />
      ))}
    </div>
  );
}
